import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import toast from 'react-hot-toast';
import FaceCapture from './FaceCapture';

const DISMISS_KEY = 'wt_face_prompt_v1';

export default function FaceEnrollPrompt() {
  const { user, updateUser } = useAuth();
  const [visible, setVisible]           = useState(false);
  const [showConsent, setShowConsent]   = useState(false);
  const [showCapture, setShowCapture]   = useState(false);

  useEffect(() => {
    if (!user) return;
    const hasFace = user.faceDescriptor?.length || user.faceDescriptorEncrypted;
    if (hasFace) return;
    if (sessionStorage.getItem(DISMISS_KEY)) return;

    const t = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(t);
  }, [user]);

  if (!visible) return null;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
    setShowConsent(false);
  };

  const handleEnroll = async (descriptor) => {
    try {
      const res = await API.put(`/users/${user.uniqueId}/face-enroll`, { descriptor });
      updateUser(res.data.user || res.data);
      toast.success('Face ID enrolled! You can now reset your PIN with your face.');
      dismiss();
    } catch {
      toast.error('Enrollment failed — please try again.');
      setShowCapture(false);
    }
  };

  if (showCapture) {
    return (
      <FaceCapture
        title="Set up Face ID"
        subtitle="This lets you reset your PIN instantly using your face."
        onCapture={handleEnroll}
        onCancel={() => { setShowCapture(false); setShowConsent(false); }}
      />
    );
  }

  if (showConsent) {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 900,
        background: 'rgba(0,0,0,0.7)', display: 'flex',
        alignItems: 'flex-end', justifyContent: 'center',
        padding: '0 0 env(safe-area-inset-bottom, 0)',
      }}>
        <div style={{
          background: 'var(--surface)', borderRadius: '20px 20px 0 0',
          padding: '28px 20px 32px', width: '100%', maxWidth: 480,
        }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{
              width: 56, height: 56, borderRadius: 16,
              background: 'rgba(255,78,138,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 12px',
              fontSize: '1.8rem',
            }}>🔐</div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', margin: '0 0 8px' }}>
              Face ID Consent
            </h3>
          </div>
          <div style={{
            background: 'rgba(255,78,138,0.06)', border: '1px solid rgba(255,78,138,0.2)',
            borderRadius: 12, padding: '14px 16px', marginBottom: 20,
            fontSize: '0.8rem', color: 'var(--text2)', lineHeight: 1.7,
          }}>
            <p style={{ margin: '0 0 8px' }}>
              <strong>What we store:</strong> A mathematical representation of your face
              (128 numbers). Not a photo.
            </p>
            <p style={{ margin: '0 0 8px' }}>
              <strong>Encryption:</strong> Your face data is encrypted with AES-256 before
              being stored on our servers.
            </p>
            <p style={{ margin: '0 0 8px' }}>
              <strong>Purpose:</strong> Face ID is used only to reset your PIN. It is never
              shared with any third party.
            </p>
            <p style={{ margin: 0 }}>
              <strong>Removal:</strong> You can delete your Face ID at any time from
              Profile → Security.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={dismiss}
              style={{
                flex: 1, padding: '12px 0', borderRadius: 50,
                background: 'none', border: '1px solid var(--border)',
                color: 'var(--text2)', fontSize: '0.88rem',
                cursor: 'pointer', fontFamily: 'var(--font-body)',
              }}
            >
              Decline
            </button>
            <button
              onClick={() => setShowCapture(true)}
              style={{
                flex: 2, padding: '12px 0', borderRadius: 50,
                background: 'linear-gradient(135deg,var(--accent),var(--accent2))',
                border: 'none', color: '#fff', fontSize: '0.88rem',
                fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)',
              }}
            >
              I Understand — Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed', bottom: '80px', left: '16px', zIndex: 800,
      maxWidth: '290px', width: 'calc(100vw - 32px)',
      background: 'var(--glass-bg)',
      border: '1px solid var(--glass-border)',
      borderRadius: '16px', padding: '14px 14px 14px 16px',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18), 0 8px 36px rgba(0,0,0,0.55)',
      backdropFilter: 'blur(32px)', WebkitBackdropFilter: 'blur(32px)',
      animation: 'promptSlideUp 0.45s cubic-bezier(0.34,1.56,0.64,1)',
    }}>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
        <span style={{ fontSize: '1.5rem', flexShrink: 0, lineHeight: 1.2 }}>🔐</span>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: '0.84rem', fontWeight: 700, color: 'var(--text)', marginBottom: '3px' }}>
            Set up Face ID
          </p>
          <p style={{ fontSize: '0.73rem', color: 'var(--text3)', lineHeight: 1.55, marginBottom: '11px' }}>
            Reset your PIN instantly using your face — no security question needed.
          </p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setShowConsent(true)}
              style={{
                background: 'linear-gradient(135deg,var(--accent),var(--accent2))',
                color: 'var(--bg)', border: 'none', borderRadius: '8px',
                padding: '6px 16px', fontSize: '0.76rem', fontWeight: 700,
                cursor: 'pointer', fontFamily: 'var(--font-body)',
              }}
            >Set Up →</button>
            <button
              onClick={dismiss}
              style={{
                background: 'none', border: '1px solid var(--border)',
                color: 'var(--text3)', borderRadius: '8px',
                padding: '6px 12px', fontSize: '0.76rem',
                cursor: 'pointer', fontFamily: 'var(--font-body)',
              }}
            >Later</button>
          </div>
        </div>
        <button
          onClick={dismiss}
          style={{
            background: 'none', border: 'none', color: 'var(--text3)',
            cursor: 'pointer', fontSize: '0.9rem', padding: '2px',
            flexShrink: 0, lineHeight: 1,
          }}
        >✕</button>
      </div>
      <style>{`
        @keyframes promptSlideUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
